import React, { useEffect, useState } from "react";
import { Box, Button, FormControlLabel, MenuItem, Radio, RadioGroup, Select, Stack, Typography, useTheme } from "@mui/material";
import { getAssetHierarchyPalette, getAxionFieldAlignSx, getContainedButtonSx, getMenuSx, getOutlinedButtonSx, getRadioSx, getSelectSx } from "../../cmms/assetHierarchyStyles";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import { createStockMovement } from "../../../fetchers/cmmsSparePart";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import {
  AdjustmentDirection,
  StockMovementType,
  getStockMovementTypeOptions,
  toSignedQuantity,
  validateStockMovementForm,
} from "../stockMovements";
import FieldError from "./FieldError";

/*
 * Book goods received, an adjustment or a return into the stock ledger.
 *
 * The quantity field is always a positive magnitude and the direction radio carries the sign.
 * A single signed box would put "2" and "-2" one keystroke apart on the one field where the
 * difference is the entire meaning, and the ledger has no update path to correct it with — a
 * mistake is corrected by a further row, which is what keeps the balance reconstructible and
 * also what makes a typo permanent.
 *
 * The radio only appears for an adjustment. Goods received and returns are positive by the
 * service's own rule, so a control that cannot change anything should not be standing there
 * inviting a click — and toSignedQuantity ignores a stale direction for exactly that reason.
 */
export default function StockMovementFormDialog({ open, onClose, sparePart, companyKey, initialType }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    movementType: StockMovementType.Receipt,
    direction: AdjustmentDirection.Decrease,
    quantity: "",
    reason: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({
      movementType: initialType ?? StockMovementType.Receipt,
      // Decrease by default: an adjustment is nearly always something gone, and the direction
      // is stated in the form rather than assumed from the sign of a number nobody typed.
      direction: AdjustmentDirection.Decrease,
      quantity: "",
      reason: "",
    });
    setErrors({});
  }, [open, initialType]);

  const handleField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handleTextChange = (event) => {
    const { name, id, value } = event.target;
    handleField(name || id, value);
  };

  const isAdjustment = form.movementType === StockMovementType.Adjustment;

  const bookMutation = useMutation({
    mutationFn: (payload) => createStockMovement(sparePart.id, payload),
  });

  const handleSubmit = async () => {
    const next = validateStockMovementForm(form, intl);
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const payload = {
      companyKey,
      movementType: form.movementType,
      quantity: toSignedQuantity(form.movementType, form.direction, form.quantity),
      reason: form.reason?.trim() || null,
      // No movedByUserId — this is a ledger, and who moved it comes from the token.
    };

    try {
      await bookMutation.mutateAsync(payload);
      // Both, always. The register grid's balance is derived from this ledger, so a booking
      // that refreshes only the drawer leaves the list showing a number that is no longer
      // true — the cmms/poc fault in another shape: two views of one thing, one updated.
      await queryClient.invalidateQueries({ queryKey: ["cmmsSpareParts"] });
      await queryClient.invalidateQueries({ queryKey: ["cmmsStockMovements", companyKey, sparePart.id] });
      pushNotification(intl.formatMessage(messages.toastMovementBooked));
      onClose?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.movementSaveFailed)));
    }
  };

  const submitting = bookMutation.isPending || bookMutation.isLoading;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(messages.movementTitle)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {sparePart?.partNumber} · {sparePart?.name}
        </Typography>

        {/*
          * The two caption-labelled blocks carry the alignment offset the Axion fields below them
          * bake in themselves, so every field in the stack starts the same distance from the one
          * above it. Without it the dialog steps 16px, 16px, then 27px twice.
          */}
        <Box sx={getAxionFieldAlignSx()}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...messages.movementType} />
          </Typography>
          <Select
            fullWidth
            size="small"
            value={form.movementType}
            onChange={(event) => handleField("movementType", event.target.value)}
            sx={getSelectSx(cmmsColors)}
            MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
          >
            {getStockMovementTypeOptions(intl).map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </Box>

        {isAdjustment &&
          <Box sx={getAxionFieldAlignSx()}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <FormattedMessage {...messages.movementDirection} />
            </Typography>
            <RadioGroup
              row
              value={form.direction}
              onChange={(event) => handleField("direction", event.target.value)}
            >
              <FormControlLabel
                value={AdjustmentDirection.Increase}
                control={<Radio size="small" sx={getRadioSx(cmmsColors)} />}
                label={intl.formatMessage(messages.movementDirectionIncrease)}
              />
              <FormControlLabel
                value={AdjustmentDirection.Decrease}
                control={<Radio size="small" sx={getRadioSx(cmmsColors)} />}
                label={intl.formatMessage(messages.movementDirectionDecrease)}
              />
            </RadioGroup>
          </Box>
        }

        <Box>
          <AxionControls.AxionTextBox
            name="quantity"
            label={`${intl.formatMessage(messages.movementQuantity)}${sparePart?.unit ? ` (${sparePart.unit})` : ""}`}
            value={form.quantity}
            onChange={handleTextChange}
          />
          <FieldError message={errors.quantity} />
        </Box>

        <Box>
          <AxionControls.AxionMultiLineText
            name="reason"
            label={intl.formatMessage(isAdjustment ? messages.movementReasonRequired : messages.movementReason)}
            value={form.reason}
            rows={2}
            onChange={handleTextChange}
          />
          <FieldError message={errors.reason} />
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.save} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
